import React, { useEffect, useState } from "react";
import { Box, Flex, Badge, Link, Spinner } from "theme-ui";
import { themevals } from "theme/themevals";
import { getVersionHistory } from "api/QuoteExpiration";
import P from "components/typography/P";
import H5 from "components/typography/H5";
import moment from "moment-timezone";

interface QuoteVersion {
  id: string;
  sequence: number;
  majorVersion: number;
  minorVersion: number;
  status: number;
  createdAt: string;
  expiresAt?: string | null;
  expiredAt?: string | null;
}

const statusBadge = (status: number) => {
  switch (status) {
    case 0: return <Badge as="span" variant="warning" sx={{ fontSize: 10 }}>New</Badge>;
    case 1: return <Badge as="span" variant="success" sx={{ fontSize: 10 }}>Accepted</Badge>;
    case 2: return <Badge as="span" variant="danger" sx={{ fontSize: 10 }}>Denied</Badge>;
    case 3: return <Badge as="span" variant="ltgrey" sx={{ fontSize: 10 }}>Draft</Badge>;
    case 4: return <Badge as="span" variant="orange" sx={{ fontSize: 10 }}>Expired</Badge>;
    case 5: return <Badge as="span" variant="ltgrey" sx={{ fontSize: 10 }}>Superseded</Badge>;
    case 6: return <Badge as="span" variant="info" sx={{ fontSize: 10 }}>Price Locked</Badge>;
    default: return <Badge as="span" variant="ltgrey" sx={{ fontSize: 10 }}>Unknown</Badge>;
  }
};

interface Props {
  quoteRequestId: string;
  currentQuoteId?: string;
}

const QuoteVersionHistory = ({ quoteRequestId, currentQuoteId }: Props) => {
  const [versions, setVersions] = useState<QuoteVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const res = await getVersionHistory(quoteRequestId);
      setVersions(Array.isArray(res) ? res : []);
      setLoading(false);
    };
    if (quoteRequestId) load();
  }, [quoteRequestId]);

  if (loading) {
    return (
      <Box sx={{ py: 2 }}>
        <Spinner size={16} strokeWidth={3} />
      </Box>
    );
  }

  if (versions.length <= 1) return null;

  // Group by major version
  const grouped: Record<number, QuoteVersion[]> = {};
  for (const v of versions) {
    if (!grouped[v.majorVersion]) grouped[v.majorVersion] = [];
    grouped[v.majorVersion].push(v);
  }

  const majorKeys = Object.keys(grouped).map(Number).sort((a, b) => b - a);

  return (
    <Box>
      <Flex
        sx={{ alignItems: "center", cursor: "pointer", gap: "8px", mb: expanded ? 2 : 0 }}
        onClick={() => setExpanded(!expanded)}
      >
        <H5
          fontFamily="Poppins"
          color={themevals.colors.dkgrey}
          fontSize="12px"
          fontWeight={600}
          marginTop="0px"
          marginBottom="0px"
        >
          Version History ({versions.length})
        </H5>
        <Box sx={{ fontSize: 10, color: themevals.colors.dkgrey }}>
          {expanded ? "▲" : "▼"}
        </Box>
      </Flex>

      {expanded && (
        <Box sx={{ pl: 0 }}>
          {majorKeys.map((major) => (
            <Box key={major} sx={{ mb: 2 }}>
              {majorKeys.length > 1 && (
                <P fontFamily="Poppins" color={themevals.colors.dkgrey} fontSize="11px" fontWeight={600} marginTop="4px" marginBottom="4px">
                  v{major}.x
                </P>
              )}
              {grouped[major].sort((a, b) => b.minorVersion - a.minorVersion).map((v) => {
                const isCurrent = v.id === currentQuoteId;
                const vLabel = `v${v.majorVersion}.${v.minorVersion}`;
                return (
                  <Flex
                    key={v.id}
                    sx={{
                      alignItems: "center",
                      gap: "8px",
                      py: "4px",
                      px: "8px",
                      borderRadius: 4,
                      bg: isCurrent ? "rgba(0,0,0,0.04)" : "transparent",
                      "&:hover": { bg: "rgba(0,0,0,0.03)" },
                    }}
                  >
                    <Link
                      href={`/quotes/${v.id}`}
                      sx={{
                        fontFamily: "Poppins",
                        fontSize: "12px",
                        fontWeight: isCurrent ? 700 : 500,
                        color: themevals.colors.black,
                        textDecoration: isCurrent ? "none" : "underline",
                        minWidth: "40px",
                      }}
                    >
                      {vLabel}
                    </Link>
                    {statusBadge(v.status)}
                    <Box sx={{ fontSize: "11px", fontFamily: "Poppins", color: themevals.colors.dkgrey, ml: "auto" }}>
                      {moment(v.createdAt).format("MMM D, YY")}
                    </Box>
                  </Flex>
                );
              })}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default QuoteVersionHistory;
